import { useEffect, useRef, useState } from "react";
import { Popup } from "@/components/common/Popup";
import { AgendaInfoSchema } from "./AgendaSchema";
import { AgendaInfo } from "./AgendaInfo";
import { GripVertical } from "lucide-react";
import {Plus} from "lucide-react";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  apiFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
  eventId: string ;
  onError?: (message: string) => void;
};



export function AgendaSetupPopup({ isOpen, onClose, apiFetch, eventId,  onError }: Props) {
  const [agendas, setAgendas] = useState<AgendaInfoSchema[]>([]);
  const [loading, setLoading] = useState(false);
  const [editAgendaId, setAgendaId] = useState(null as number | null);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const wasOpenRef = useRef(false);

  const closeEdit = () => {
    if(editAgendaId)
    setAgendaId(null);
  else
    onClose();
  };

  const onAgendaClosed = (agenda: AgendaInfoSchema | null) => {
    setAgendaId(null);
    if (!agenda) return;

//replace title date and tabname of the agenda in the list
    loadAgendas();
  }
  const moveItem = (list: AgendaInfoSchema[], fromIndex: number, toIndex: number) => {
    const updated = [...list];
    const [moved] = updated.splice(fromIndex, 1);
    updated.splice(toIndex, 0, moved);
    return updated;
  };

  const reorderAgenda = async (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;

    const movedAgenda = agendas[fromIndex];
    if (!movedAgenda?.Id) return;

    const previous = agendas;
    const next = moveItem(agendas, fromIndex, toIndex);
    setAgendas(next);

    try {
      const fd = new FormData();
      fd.append("toIndex", String(toIndex));
      fd.append("fromIndex", String(fromIndex));
      fd.append("groupId", String(movedAgenda.Id));
      fd.append("eventId", eventId);

      const res = await apiFetch(`/api/eventpool/AgendaGroupReorder`, {
        method: "POST",
        body: fd,
      });

      if (!res.ok) {
        throw new Error(await res.text());
      }
    } catch (err) {
      console.error(err);
      setAgendas(previous);
      onError?.("Failed to reorder agendas");
    }
  };

  const handleDrop = async (targetIndex: number) => {
    if (draggedIndex == null) return;
    await reorderAgenda(draggedIndex, targetIndex);
  };


 const loadAgendas = async () => {
      try {
        setLoading(true);

        
        const res = await apiFetch(`/api/EventPool/GetAgendaGroups?eventId=${eventId}`);
        if (!res.ok) {
          setAgendas([]);
          return;
        }

        const data = await res.json();
       

        setAgendas(data );

      } catch (err) {
        console.error(err);
        
          setAgendas([]);
          onError?.("Failed to load agendas");
        
      } finally {
         setLoading(false);
      }
    };

  useEffect(() => {
    const openedNow = isOpen && !wasOpenRef.current;
    if (openedNow) {
      console.log("Loading agendas for event", eventId);
      loadAgendas();
    }

    wasOpenRef.current = isOpen;
  }, [isOpen, eventId]);

  return (
    <Popup isOpen={isOpen} onClose={closeEdit} title={editAgendaId != null ? " Agenda" : "Agenda Groups"} className="w-[95vw] max-w-6xl">
      {editAgendaId == null && (
      <div className="overflow-x-auto">
        <div className="float-right cursor-pointer mb-5"><Plus onClick={() => setAgendaId(-1)}></Plus></div>
        <table className="min-w-full text-sm">
          <thead className="bg-slate-100 text-left">
            <tr>
              <th className="px-3 py-2 w-[100px]">Sort</th>
              <th className="px-3 py-2 w-[200px]">Tab Name</th>
              <th className="px-3 py-2 w-[150px]">Date</th>
              <th className="px-3 py-2">Title</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr className="border-t">
                <td colSpan={8} className="px-3 py-4 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : (!agendas || agendas.length === 0 )? (
              <tr className="border-t">
                <td colSpan={8} className="px-3 py-4 text-center text-gray-500">
                  No agendas yet
                </td>
              </tr>
            ) : (
              agendas.map((item, index) => (
                <tr
                  key={`${item.Id ?? "plenum"}-${index}`}
                  className={`border-t align-middle ${dragOverIndex === index ? "bg-slate-100" : ""}`}
                  onClick={() => {
                    if (draggedIndex != null) return;
                    setAgendaId(item.Id ?? null);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOverIndex(index);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void handleDrop(index);
                    setDraggedIndex(null);
                    setDragOverIndex(null);
                  }}
                  onDragLeave={() => {
                    if (dragOverIndex === index) setDragOverIndex(null);
                  }}
                  style={{ cursor: "pointer" }}
                >
                  <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                    <button
                      type="button"
                      draggable
                      className="inline-flex cursor-grab items-center text-slate-500 hover:text-slate-700"
                      onClick={(e) => e.stopPropagation()}
                      onDragStart={(e) => {
                        e.stopPropagation();
                        setDraggedIndex(index);
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      onDragEnd={() => {
                        setDraggedIndex(null);
                        setDragOverIndex(null);
                      }}
                      title="Drag to reorder"
                      aria-label="Drag to reorder"
                    >
                      <GripVertical className="h-4 w-4" />
                    </button>
                  </td>
                  
                  <td className="px-3 py-2">{item.TabName ?? ""}</td>
                  <td className="px-3 py-2">{item.Date ?? ""}</td>
                  <td className="px-3 py-2">{item.Title ?? ""}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>)}

      {editAgendaId != null && (
        <div>
          <AgendaInfo  eventId={eventId} Id={editAgendaId} onClose={(agenda) => onAgendaClosed(agenda)} />
        </div>
      )}
    </Popup>
  );
}
